/**
 * Colours for the contour-comparison difference overlay.
 *
 * Scheme: the agreement region (A∩B) is a fixed translucent-green FILL, while
 * each ROI's own contour is drawn as an OUTLINE in its OWN colour. Clinicians
 * associate structures with their colours, and outlines collide far less than
 * fills, so the two contours stay readable; the agreement colour is fixed (not
 * derived from an ROI) so "both agree" always reads the same. The disagreement
 * is read off the geometry — the area inside an ROI's outline but outside the
 * green fill. Shared by the engine overlay and the side-panel legend.
 *
 * This module is intentionally dependency-free (no gl-matrix, no engine
 * imports) so it can be imported from the engine layer and the React UI
 * without crossing a framework boundary.
 */

/** The three regions of a two-ROI comparison overlay. */
export enum ComparisonRegion {
    /** Inside both ROIs — the structures agree (translucent green fill). */
    Agreement = 'agreement',
    /** ROI A's own contour, outlined in ROI A's colour. */
    RoiA = 'roiA',
    /** ROI B's own contour, outlined in ROI B's colour. */
    RoiB = 'roiB',
}

/** 8-bit RGB colour, channels in [0, 255]. */
export interface Rgb8 {
    r: number;
    g: number;
    b: number;
}

/**
 * Fixed fill colour for the agreement (A∩B) region — Okabe–Ito bluish-green,
 * colour-blind safe. Fixed rather than ROI-derived so the "both agree" region
 * is always the same recognisable green; each ROI's own contour instead uses
 * the ROI's own colour (see module doc).
 */
export const AGREEMENT_COLOR: Rgb8 = { r: 0, g: 158, b: 115 };

/** Convert an 8-bit RGB colour to GPU-normalised [0, 1] components. */
export function rgb8ToNormalized(color: Rgb8): [number, number, number] {
    return [color.r / 255, color.g / 255, color.b / 255];
}

/** Convert an 8-bit RGB colour to a CSS `rgb(...)` string for the legend. */
export function rgb8ToCss(color: Rgb8): string {
    return `rgb(${color.r}, ${color.g}, ${color.b})`;
}
